import { Component, Input, Renderer2, RendererFactory2 } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DocumentFieldEmitterService } from 'app/global/documentfield-emitter.service';
import { DomHandlersService } from 'app/global/dom-handlers.service';
import { GlobalService } from 'app/global/global.service';
import { IntegrationService } from 'app/global/integrations/integrations.service';
import { Field } from 'app/interfaces/fields';
import { MomentPipe } from 'app/pipes/moment.pipe';
import { DocumentHelperFacade } from '../../documents/document-right-sidebar/document-helper.facade';
import { IDocumentDetailRootDTO } from '../../documents/dto/document-details.dto';
import { AbstractUpdateFieldsComponent } from '../../general-editor-tabs/fields-builder-service/abstract-update-fields-data.component';
import { FieldsBuilderService } from '../../general-editor-tabs/fields-builder-service/fields-builder.service';
import { IFieldVisibilityType } from '../field-visibility.dto';
import { FieldsDataHelperService } from '../../general-editor-tabs/fields-builder-service/fields-data-helper.service';
import { IsFieldPresentPipe } from '../pipes/is-field-present.pipe';
import { EditorEmitterService } from '../../events/editor-emitter.service';
import { PopoverSettingMenu, TemplateRouteEnum } from '../edit-template.dto';

@Component({
  selector: 'app-meta-data-fields-list',
  templateUrl: './meta-data-fields-list.component.html',
  styleUrl: './meta-data-fields-list.component.scss',
})

export class MetaDataFieldsListComponent extends AbstractUpdateFieldsComponent {
  @Input() field!: Field;
  @Input() documentData: IDocumentDetailRootDTO;

  @Input() fieldVisibilityType: IFieldVisibilityType;

  @Input() get enableFieldSettings(): boolean {
    return this._enableFieldSettings;
  }

  set enableFieldSettings(value: boolean) {
    this._enableFieldSettings = value || false;
  }
  public _enableFieldSettings: boolean = false;
  
  @Input() get currentRoute(): TemplateRouteEnum {
    return this._currentRoute;
  }

  set currentRoute(value: TemplateRouteEnum) {
    this._currentRoute = value || TemplateRouteEnum.SendDocument;
  }
  public _currentRoute: TemplateRouteEnum = TemplateRouteEnum.SendDocument;
  public checkBoxField: Array<{ name: string, value: 0 | 1 }> = [
    { name: 'Yes', value: 1 },
    { name: 'No', value: 0 }
  ]

  public flatpickrDefaultInterFace = {
    monthSelectorType: 'dropdown',
  }


  public clearButton: HTMLElement = document.createElement('button');
  public renderer: Renderer2;
  public templateRouteEnum = TemplateRouteEnum;
  constructor(
    public _globalService: GlobalService,
    public documentFieldsEmitter: DocumentFieldEmitterService,
    public _momentPipe$: MomentPipe,
    public rendererFactory: RendererFactory2,
    public _integrationService: IntegrationService,
    public _modalService: NgbModal,
    public _fieldsBuilderService: FieldsBuilderService,
    public _translocoService: TranslocoService,
    public _documentHelperFacade: DocumentHelperFacade,
    private domHandlersService: DomHandlersService,
    _fieldsDataHelperService: FieldsDataHelperService,
    private _isFieldPresentPipe: IsFieldPresentPipe,
    private editorEmitter: EditorEmitterService,
  ) {
    super(
      _globalService,
      documentFieldsEmitter,
      _momentPipe$,
      rendererFactory,
      _integrationService,
      _modalService,
      _fieldsBuilderService,
      _translocoService,
      _documentHelperFacade,
      _fieldsDataHelperService
    );
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }
  /**
   * delete field
   * @param field - The field to delete.
   * @returns {void}
   * @example
   * ```typescript
   * this.deleteField(field);
   * ```
   * @returns {void}
   */
  deleteField(): void {
    if(this.field?.pinned_field === 1) return;
    this._globalService.deleteField(this.field.uuid);
    this._documentHelperFacade.emitShouldUpdateFieldsData({
      uuid: [],
      deleted_field_uuid: [this.field.uuid]
    });
  }



 
  /**
   * Sets the lock state of the current field and updates the UI accordingly.
   *
   * @param lock - The lock state to set for the field (0 for unlocked, 1 for locked).
   *
   * This method updates the `is_locked` property of the current field and persists the change
   * using the global service. It then finds all fields with the same `selected_field_type_id`
   * and field section, and updates their corresponding DOM elements by adding or removing
   * CSS classes to visually indicate the lock state.
   * 
   * @returns {void}
   */
  setFieldLock(lock: 0 | 1): void {
    if (!this.documentData?.template?.can_edit) return;
    this.field.is_locked = lock;
    this._globalService.updateField(this.field.uuid, this.field);
    this._documentHelperFacade.emitShouldUpdateFieldsData({
      uuid: [this.field.uuid],
      deleted_field_uuid: []
    });
    const existingField = this._globalService.findFieldsWithCondition(this._globalService.fields_data, 'selected_field_type_id', this.field.selected_field_type_id, this._globalService.fields_data[this.field.uuid].field_section) as Field[];
    existingField.forEach(field => {
      if(document?.querySelector(`.drag-fields[uuid="${field.uuid}"]`)){
        const selector = document.querySelector(`.drag-fields[uuid="${field.uuid}"]`) as HTMLElement;
        if(selector){
          const containers = selector.querySelectorAll('.fields-ele-container, .tag-container');
          containers.forEach((element: HTMLElement) => {
            const lockedClass = (this.sectionLockedClass || '').trim();
            if (lock === 1) {
              if (lockedClass) element.classList.add(lockedClass);
              element.classList.add('pointer-none');
            } else {
              if (lockedClass) element.classList.remove(lockedClass);
              element.classList.remove('pointer-none');
            }
          });
        }
      }
    });
  }



  get sectionLockedClass(): string {
    return this.fieldVisibilityType?.is_secret_hidden_field ? 'secret-hidden-locked' : this.fieldVisibilityType?.is_semi_hidden_field ? 'semi-hidden-locked' : '';
  }


  
  get canLock(): boolean {
    return !this.field.is_locked;
  }
  
  
  
  get canUnlock(): boolean {
    return this.shouldFieldDisabled && this.canEdit;
  }
  
  
  get isNotPresent(): boolean {
     return !this._isFieldPresentPipe.transform(this.field);
  }
  


  get canDelete(): boolean {
    return this.canEdit && (this.isNotPresent || this.field?.pinned_field === 1);
  }
  


  get eyeOffTooltip(): string {
    return this.field.secret_hidden_field
      ? this._translocoService.translate('Eye-off icon tooltip text (Edit template settings - Secret fields) - This field and its value are only visible in the document index data making it ideal for information intended solely for API use')
      : this._translocoService.translate('Eye-off icon tooltip text (Edit template settings - Secret fields) - Field is not present in the document');
  }



  /**
   * Get suggestions for the field
   * @param event - The event that triggered the suggestion.
   * @param field - The field to get suggestions for.
   * @example
   * ```typescript
   * this.getSuggestions(event, field);
   * ```
   * @returns {void}
   */
  getSuggestions(event: KeyboardEvent | MouseEvent, field: Field){
    if(field?.api_field){
      const input = document.querySelector(`.edit-template-right-menu-fields[uuid="${field.uuid}"]`) as HTMLInputElement;
      if (!input) return;
      this.domHandlersService.onTextFieldFocus(event, {role_id: 0}, field.uuid, input, 'rightBarField');
    }
  }


  get shouldFieldDisabled(): boolean {
    return this._globalService.shouldFieldDisabled(this.documentData, this.field)
  }

  get disabledRecipientField() : boolean {
    return (this.currentRoute === TemplateRouteEnum.SendDocument && (Number(this.field?.selected_for_role_id) !== 0 ));
  }

  get isFieldAssignedToRecipient(): boolean {
    const roleId = Number(this.field?.selected_for_role_id);
    return !isNaN(roleId) && roleId !== 0 && roleId !== -1;
  }

  get canEdit(): boolean {
    return this.documentData?.template?.can_edit;
  }

  openFieldSettings(field: Field): void {
    if(!this.canEdit) return;
    const field_name = field?.field_name;
    const selected_field = field?.selected_field;
    const uuid = field?.uuid;
    this.editorEmitter.triggerOpenMenuAction({ rightMenuType: PopoverSettingMenu.popoverSettingMenu, fieldSettings: {uuid, field_name, selected_field} });
  }
}
